import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// Separator removed — nodes now use collapsible cards
import FieldRenderer from '@/components/FieldRenderer';
import { useRealtimeWorkflows, type WorkflowDetail, type InputField, type AnalyzedOutput } from '@/hooks/useRealtimeWorkflows';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import WorkflowAudienceSelector, { type WorkflowAudience } from '@/components/WorkflowAudienceSelector';
import {
  ArrowLeft,
  Trash2,
  Sparkles,
  Loader2,
  Image,
  Video,
  Eye,
  Box,
  ChevronDown,
  Globe,
  GlobeLock,
  EyeOff,
  Check,
  ChevronRight,
  GripVertical,
  List,
  Plus,
  X,
  Play,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import RunPage from '@/pages/RunPage';
import { WORKFLOW_CATEGORIES } from '@/lib/workflowCategories';

const outputIcons: Record<string, typeof Image> = {
  image: Image,
  video: Video,
  preview: Eye,
  unknown: Box,
};

// A 1×1 transparent image used to suppress the browser's native drag ghost, so
// reordering reads as a pure vertical list move (source dims in place) instead
// of a floating card that drifts horizontally and spills outside the container.
const TRANSPARENT_DRAG_IMAGE =
  typeof window !== 'undefined'
    ? Object.assign(new window.Image(), {
        src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      })
    : null;

function hideNativeDragImage(e: React.DragEvent) {
  if (TRANSPARENT_DRAG_IMAGE) {
    e.dataTransfer.setDragImage(TRANSPARENT_DRAG_IMAGE, 0, 0);
  }
}

function groupFieldsByNode(fields: InputField[]): { nodeId: string; label: string; fields: InputField[] }[] {
  const groups = new Map<string, { nodeId: string; label: string; fields: InputField[] }>();

  for (const field of fields) {
    if (!groups.has(field.nodeId)) {
      const nodeLabel = field.label.split(' — ')[0];
      groups.set(field.nodeId, {
        nodeId: field.nodeId,
        label: nodeLabel,
        fields: [],
      });
    }
    groups.get(field.nodeId)!.fields.push(field);
  }

  return [...groups.values()];
}

interface AllowedValuesEditorProps {
  field: InputField;
  onToggle: () => void;
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  onSetDefault: (value: string) => void;
  onToggleEnrichedOption: (value: string) => void;
}

function AllowedValuesEditor({
  field,
  onToggle,
  onAdd,
  onRemove,
  onSetDefault,
  onToggleEnrichedOption,
}: AllowedValuesEditorProps) {
  const [draft, setDraft] = useState('');
  const isConstrained = field.widget === 'select' && field.baseWidget !== undefined;
  const hasEnrichedOptions = field.widget === 'select' && (field.enrichedOptions?.length ?? 0) > 0;
  // Only text/number-like widgets can be constrained to a value list.
  const canConstrain = ['text', 'textarea', 'number'].includes(field.baseWidget ?? field.widget);

  if (!isConstrained && !canConstrain && !hasEnrichedOptions) return null;

  const commitDraft = () => {
    onAdd(draft);
    setDraft('');
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/40">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] text-muted-foreground">
          {hasEnrichedOptions ? 'Available Options (from enrichment)' : 'Allowed Values'}
        </label>
        {!hasEnrichedOptions && (
          <button
            type="button"
            onClick={onToggle}
            className={
              'flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded transition-colors ' +
              (isConstrained
                ? 'text-violet-600 bg-violet-50 hover:bg-violet-100 dark:text-violet-400 dark:bg-violet-500/10'
                : 'text-muted-foreground bg-accent hover:bg-accent/80')
            }
          >
            <List className="h-3 w-3" />
            {isConstrained ? 'Restricted to list' : 'Free input'}
          </button>
        )}
      </div>

      {hasEnrichedOptions && (
        <div className="space-y-2">
          <p className="text-[11px] text-muted-foreground/60 mb-3">
            Select which options from ComfyUI enrichment to show to users. Click an enabled option to set it as default.
          </p>
          <div className="space-y-1.5">
            {field.enrichedOptions!.map((opt) => {
              const isEnabled = field.options?.includes(opt) ?? false;
              const isDefault = String(field.defaultValue) === opt;
              return (
                <div
                  key={opt}
                  className={
                    'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ' +
                    (isEnabled
                      ? isDefault
                        ? 'border-violet-300 bg-violet-50 dark:border-violet-500/40 dark:bg-violet-500/10'
                        : 'border-border/60 bg-card hover:bg-accent/50'
                      : 'border-dashed border-border/40 bg-muted/30')
                  }
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => onToggleEnrichedOption(opt)}
                    className="h-4 w-4 rounded border-border text-violet-600 focus:ring-violet-500 cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={() => isEnabled && onSetDefault(opt)}
                    disabled={!isEnabled}
                    className="flex-1 text-left text-sm"
                  >
                    <span className={isEnabled ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                      {opt}
                      {isDefault && <Check className="inline-block h-3 w-3 ml-1.5 text-violet-600" />}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground/50 mt-2">
            {field.options?.length ?? 0} of {field.enrichedOptions!.length} options enabled. Click an enabled option to make it
            the default.
          </p>
        </div>
      )}

      {isConstrained && !hasEnrichedOptions && (
        <div className="space-y-2">
          {(field.options?.length ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {field.options!.map((opt) => {
                const isDefault = String(field.defaultValue) === opt;
                return (
                  <span
                    key={opt}
                    className={
                      'group flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ' +
                      (isDefault
                        ? 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-300'
                        : 'border-border/60 bg-background text-foreground')
                    }
                  >
                    <button
                      type="button"
                      onClick={() => onSetDefault(opt)}
                      title={isDefault ? 'Default value' : 'Set as default'}
                      className="flex items-center gap-1"
                    >
                      {isDefault && <Check className="h-3 w-3" />}
                      {opt}
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemove(opt)}
                      className="text-muted-foreground/50 hover:text-destructive"
                      title="Remove value"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground/60 italic">No values yet — add options users can pick from.</p>
          )}

          <div className="flex items-center gap-2">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraft();
                }
              }}
              placeholder="Add an allowed value…"
              className="text-sm h-8"
            />
            <Button type="button" size="sm" variant="outline" onClick={commitDraft} disabled={!draft.trim()}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground/50">
            Click a value to make it the default. The tinted chip is the default.
          </p>
        </div>
      )}
    </div>
  );
}

interface NumericConstraintsEditorProps {
  field: InputField;
  onUpdateConstraints: (updates: { min?: number; max?: number; step?: number }) => void;
}

function NumericConstraintsEditor({ field, onUpdateConstraints }: NumericConstraintsEditorProps) {
  const isNumeric = field.widget === 'number' || field.widget === 'slider' || field.widget === 'seed';

  if (!isNumeric) return null;

  const hasConstraints = field.min !== undefined || field.max !== undefined || field.step !== undefined;

  // enrichedMin: Original min from ComfyUI (fixed, not adjustable)
  // enrichedMax: Original max from ComfyUI (slider upper bound)
  // field.max: Current max value set by manager (slider position)
  // If enrichedMax is not set, use the defaultValue as the original upper bound
  // to prevent the slider range from changing when manager adjusts the constraint
  const enrichedMin = field.min ?? 0;
  const enrichedMax = field.enrichedMax ?? Math.max(field.max ?? 0, Number(field.defaultValue) || 0);
  const currentMax = field.max ?? enrichedMax;
  const currentStep = field.step ?? 1;

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    // Max can't be less than min
    const newMax = Math.max(value, enrichedMin);
    onUpdateConstraints({ max: newMax });
  };

  // Clamp the slider value to prevent browser errors when currentMax > enrichedMax
  const sliderValue = Math.min(currentMax, enrichedMax);

  return (
    <div className="mt-3 pt-3 border-t border-border/40">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] text-muted-foreground">Numeric Constraints {field.widget === 'slider' && '(Range)'}</label>
      </div>

      {hasConstraints && (
        <p className="text-[11px] text-muted-foreground/60 mb-3">
          Drag slider to set maximum value. Users will see: {enrichedMin} — {currentMax}
        </p>
      )}

      <div className="space-y-4">
        {/* Maximum Value Slider */}
        <div className="space-y-2">
          <input
            type="range"
            min={enrichedMin}
            max={enrichedMax}
            step={currentStep}
            value={sliderValue}
            onChange={handleMaxChange}
            className="w-full h-2 accent-violet-600 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground/60 font-mono">
            <span>{enrichedMin}</span>
            <span>{enrichedMax}</span>
          </div>
        </div>

        {/* Step Input */}
        <div>
          <label className="text-[10px] text-muted-foreground block mb-1">Step</label>
          <Input
            type="number"
            value={field.step ?? ''}
            onChange={(e) => {
              const val = e.target.value === '' ? undefined : Number(e.target.value);
              onUpdateConstraints({ step: val });
            }}
            placeholder="Default: 1"
            className="text-sm h-8"
          />
          <p className="text-[10px] text-muted-foreground/50 mt-1">
            Increment value for the slider (e.g., 0.1 for decimals, 5 for larger steps)
          </p>
        </div>
      </div>
    </div>
  );
}

export default function WorkflowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getWorkflow, deleteWorkflow, enrichWorkflow, updateWorkflow, toggleHideWorkflow } = useRealtimeWorkflows();

  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrichmentFailed, setEnrichmentFailed] = useState(false);
  const [rawExpanded, setRawExpanded] = useState(false);
  const [audienceExpanded, setAudienceExpanded] = useState(true);
  const [inputFormExpanded, setInputFormExpanded] = useState(true);
  const [outputsExpanded, setOutputsExpanded] = useState(true);

  const [draftName, setDraftName] = useState('');
  const [draftDesc, setDraftDesc] = useState('');
  const [showOutputDropdown, setShowOutputDropdown] = useState(false);
  const [outputSearchQuery, setOutputSearchQuery] = useState('');
  const [draftOwner, setDraftOwner] = useState('');
  const [draftOwnerName, setDraftOwnerName] = useState('');
  const [draftStatus, setDraftStatus] = useState<string>('draft');
  const [draftMask, setDraftMask] = useState<InputField[]>([]);
  const [originalMask, setOriginalMask] = useState<InputField[]>([]); // Store pre-enrichment mask
  // Edited text values baked into nodes (keyed by fieldId "nodeId.inputName").
  // Lets a manager fix a wrong prompt without re-uploading from ComfyUI.
  const [draftRawOverrides, setDraftRawOverrides] = useState<Record<string, string>>({});
  const [draftOutputNodes, setDraftOutputNodes] = useState<AnalyzedOutput[]>([]);
  const [draftAudience, setDraftAudience] = useState<WorkflowAudience>({ type: 'everyone' });
  const [draftCategories, setDraftCategories] = useState<string[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [testVersion, setTestVersion] = useState<'original' | 'enriched'>('enriched'); // Which version to test
  const [dragFieldId, setDragFieldId] = useState<string | null>(null);
  const [dragNodeId, setDragNodeId] = useState<string | null>(null);
  // A row is only draggable while the pointer went down on its grip handle —
  // otherwise selecting text inside an input would start a drag.
  const [dragHandle, setDragHandle] = useState<{ type: 'node' | 'field'; id: string } | null>(null);
  // Move the dragged row on the Y axis only. Native DnD can't constrain its
  // ghost, so we hide the ghost and translate the real element imperatively via
  // a ref — using state would re-render the whole list on every drag tick.
  const draggedElRef = useRef<HTMLElement | null>(null);
  const dragStartYRef = useRef<number | null>(null);
  const dragRafRef = useRef<number | null>(null);
  // Scroll the Outputs card fully into view when the picker opens near the bottom.
  const outputCardRef = useRef<HTMLDivElement | null>(null);

  const beginVerticalDrag = (el: HTMLElement, clientY: number) => {
    draggedElRef.current = el;
    dragStartYRef.current = clientY;
    // The lifted row follows the cursor, so it sits under the pointer and would
    // swallow the dragover/drop meant for the row beneath — let those pass
    // through. Applied on the NEXT frame: mutating the dragged element (its
    // hit-testing) synchronously inside dragstart cancels the drag in Chrome.
    dragRafRef.current = requestAnimationFrame(() => {
      if (draggedElRef.current === el) el.style.pointerEvents = 'none';
    });
  };

  const moveVerticalDrag = (clientY: number) => {
    // clientY is 0 on the final drag event — ignore it to avoid a jump.
    if (!draggedElRef.current || dragStartYRef.current === null || clientY === 0) return;
    draggedElRef.current.style.transform = `translateY(${clientY - dragStartYRef.current}px)`;
  };

  const endVerticalDrag = () => {
    if (dragRafRef.current !== null) cancelAnimationFrame(dragRafRef.current);
    dragRafRef.current = null;
    if (draggedElRef.current) {
      draggedElRef.current.style.transform = '';
      draggedElRef.current.style.pointerEvents = '';
    }
    draggedElRef.current = null;
    dragStartYRef.current = null;
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setEnrichmentFailed(false);
    try {
      const data = await getWorkflow(id);

      // If workflow is not enriched, automatically enrich it
      if (!data.enriched) {
        setEnriching(true);
        setOriginalMask(data.inputMask); // Store original before enriching
        try {
          const enrichedData = await enrichWorkflow(id);
          // Use enriched data
          setWorkflow(enrichedData);
          setDraftName(enrichedData.name);
          setDraftDesc((enrichedData as unknown as { description?: string }).description || '');
          const owner = (enrichedData as unknown as { owner?: string }).owner || '';
          const ownerName = (enrichedData as unknown as { ownerName?: string }).ownerName || owner;
          setDraftOwner(owner);
          setDraftOwnerName(ownerName);
          setDraftStatus((enrichedData as unknown as { status?: string }).status || 'draft');
          setDraftMask(enrichedData.inputMask);
          setDraftOutputNodes(enrichedData.analysis?.outputNodes || []);
          setDraftCategories(enrichedData.categories || []);
          const audience = (enrichedData as unknown as { audience?: WorkflowAudience }).audience;
          setDraftAudience(audience || { type: 'specific', userIds: owner ? [owner] : [] });
        } catch (enrichErr) {
          // Enrichment failed, fall back to original data and show manual button
          setEnrichmentFailed(true);
          setWorkflow(data);
          setDraftName(data.name);
          setDraftDesc((data as unknown as { description?: string }).description || '');
          const owner = (data as unknown as { owner?: string }).owner || '';
          const ownerName = (data as unknown as { ownerName?: string }).ownerName || owner;
          setDraftOwner(owner);
          setDraftOwnerName(ownerName);
          setDraftStatus((data as unknown as { status?: string }).status || 'draft');
          setDraftMask(data.inputMask);
          setDraftOutputNodes(data.analysis?.outputNodes || []);
          setDraftCategories(data.categories || []);
          const audience = (data as unknown as { audience?: WorkflowAudience }).audience;
          setDraftAudience(audience || { type: 'specific', userIds: owner ? [owner] : [] });
        } finally {
          setEnriching(false);
        }
      } else {
        // Already enriched, use as-is
        setWorkflow(data);
        setDraftName(data.name);
        setDraftDesc((data as unknown as { description?: string }).description || '');
        const owner = (data as unknown as { owner?: string }).owner || '';
        const ownerName = (data as unknown as { ownerName?: string }).ownerName || owner;
        setDraftOwner(owner);
        setDraftOwnerName(ownerName);
        setDraftStatus((data as unknown as { status?: string }).status || 'draft');
        setDraftMask(data.inputMask);
        // Store original mask (pre-enrichment version) by detecting non-enriched fields
        const hasEnrichedFields = data.inputMask.some(
          (f: InputField) => f.widget === 'select' && f.options && f.options.length > 0,
        );
        if (!hasEnrichedFields) {
          setOriginalMask(data.inputMask);
        }
        setDraftOutputNodes(data.analysis?.outputNodes || []);
        setDraftCategories(data.categories || []);
        const audience = (data as unknown as { audience?: WorkflowAudience }).audience;
        setDraftAudience(audience || { type: 'specific', userIds: owner ? [owner] : [] });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  }, [id, getWorkflow, enrichWorkflow]);

  useEffect(() => {
    load();
  }, [load]);

  // When the output picker opens, scroll it fully into view (it expands inline
  // and may sit below the fold near the page bottom).
  useEffect(() => {
    if (!showOutputDropdown) return;
    const el = outputCardRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'end' }));
    return () => cancelAnimationFrame(raf);
  }, [showOutputDropdown]);

  const handleEnrich = async () => {
    if (!id) return;
    setEnriching(true);
    setError(null);
    try {
      // Save original mask before enriching
      if (originalMask.length === 0) {
        setOriginalMask(draftMask);
      }
      const data = await enrichWorkflow(id);

      // Update all state with enriched data
      setWorkflow(data);
      setDraftName(data.name);
      setDraftDesc((data as unknown as { description?: string }).description || '');
      const owner = (data as unknown as { owner?: string }).owner || '';
      const ownerName = (data as unknown as { ownerName?: string }).ownerName || owner;
      setDraftOwner(owner);
      setDraftOwnerName(ownerName);
      setDraftStatus((data as unknown as { status?: string }).status || 'draft');
      setDraftMask(data.inputMask); // Update draft mask so edits work on enriched fields
      setDraftOutputNodes(data.analysis?.outputNodes || []);
      // Default to specific people with owner included if no audience is set
      const audience = (data as unknown as { audience?: WorkflowAudience }).audience;
      setDraftAudience(audience || { type: 'specific', userIds: owner ? [owner] : [] });
      setEnrichmentFailed(false); // Reset failure state on success
    } catch {
      setError('Enrichment failed — is your ComfyUI endpoint configured?');
      setEnrichmentFailed(true);
    } finally {
      setEnriching(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      const data = await updateWorkflow(id, {
        name: draftName.trim(),
        description: draftDesc,
        // owner is read-only, not sent in updates
        status: draftStatus,
        inputMask: draftMask,
        outputNodes: draftOutputNodes,
        categories: draftCategories,
        audience: draftAudience,
        // Only send baked-value edits when there are any.
        ...(Object.keys(draftRawOverrides).length > 0 ? { rawInputOverrides: draftRawOverrides } : {}),
      });
      setDraftRawOverrides({});
      setWorkflow(data);
      setDraftName(data.name);
      setDraftDesc((data as unknown as { description?: string }).description || '');
      const owner = (data as unknown as { owner?: string }).owner || '';
      const ownerName = (data as unknown as { ownerName?: string }).ownerName || owner;
      setDraftOwner(owner);
      setDraftOwnerName(ownerName);
      setDraftStatus((data as unknown as { status?: string }).status || 'draft');
      setDraftMask(data.inputMask);
      setDraftOutputNodes(data.analysis?.outputNodes || []);
      setDraftCategories(data.categories || []);
      // Default to specific people with owner included if no audience is set
      const audience = (data as unknown as { audience?: WorkflowAudience }).audience;
      setDraftAudience(audience || { type: 'specific', userIds: owner ? [owner] : [] });
    } catch {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteWorkflow(id);
      navigate('/admin/workflows');
    } catch {
      setError('Failed to delete workflow');
      setDeleting(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!id || !workflow) return;

    // Optional: Confirmation for archiving published workflows
    if (!(workflow as WorkflowDetail & { hidden?: boolean }).hidden && draftStatus === 'published') {
      const confirmed = window.confirm('This workflow is published. Archiving it will hide it from users. Continue?');
      if (!confirmed) return;
    }

    setArchiving(true);
    try {
      const isCurrentlyHidden = (workflow as WorkflowDetail & { hidden?: boolean }).hidden === true;
      await toggleHideWorkflow(id, !isCurrentlyHidden);
      // Refresh workflow data
      const updated = await getWorkflow(id);
      setWorkflow(updated);
    } catch {
      setError(`Failed to ${(workflow as WorkflowDetail & { hidden?: boolean }).hidden ? 'unarchive' : 'archive'} workflow`);
    } finally {
      setArchiving(false);
    }
  };

  const handleToggleVisibility = (fieldId: string) => {
    setDraftMask((prev) => prev.map((f) => (f.id === fieldId ? { ...f, visible: !f.visible } : f)));
  };

  // The value currently baked into the node's graph for this field.
  const bakedValue = (field: InputField): string => {
    const raw = (workflow?.rawWorkflow || {}) as Record<string, { inputs?: Record<string, unknown> }>;
    const val = raw[field.nodeId]?.inputs?.[field.inputName];
    return typeof val === 'string' ? val : '';
  };

  // Edit the text value stored in the node itself (persisted to rawWorkflow on
  // save). We track only genuine changes so unedited fields aren't sent. The
  // mask's defaultValue is kept in sync so the run-page seed (visible fields)
  // and the read-only preview reflect the fix too.
  const handleEditRawValue = (field: InputField, value: string) => {
    setDraftRawOverrides((prev) => {
      const next = { ...prev };
      if (value === bakedValue(field)) delete next[field.id];
      else next[field.id] = value;
      return next;
    });
    setDraftMask((prev) => prev.map((f) => (f.id === field.id ? { ...f, defaultValue: value } : f)));
  };

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  };

  // Rebase the follow-transform so the dragged box stays under the cursor right
  // after the list reshuffles beneath it (its natural slot just moved).
  const rebaseVerticalDrag = (clientY: number) => {
    dragStartYRef.current = clientY;
    if (draggedElRef.current) draggedElRef.current.style.transform = 'translateY(0px)';
  };

  // Live reorder: move the dragged field before/after the hovered target as the
  // cursor crosses its midpoint. Returns true if the order actually changed.
  const reorderField = (dragId: string, targetId: string, after: boolean, clientY: number) => {
    if (dragId === targetId) return;
    let changed = false;
    setDraftMask((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const dragIdx = sorted.findIndex((f) => f.id === dragId);
      if (dragIdx === -1) return prev;
      const [moved] = sorted.splice(dragIdx, 1);
      const targetIdx = sorted.findIndex((f) => f.id === targetId);
      if (targetIdx === -1) return prev;
      sorted.splice(after ? targetIdx + 1 : targetIdx, 0, moved);
      const prevSorted = [...prev].sort((a, b) => a.order - b.order);
      if (prevSorted.every((f, i) => f.id === sorted[i].id)) return prev;
      changed = true;
      return sorted.map((f, i) => ({ ...f, order: i }));
    });
    if (changed) rebaseVerticalDrag(clientY);
  };

  // Live reorder for whole node groups.
  const reorderNode = (dragId: string, targetId: string, after: boolean, clientY: number) => {
    if (dragId === targetId) return;
    let changed = false;
    setDraftMask((prev) => {
      const groups = groupFieldsByNode(prev);
      const dragIdx = groups.findIndex((g) => g.nodeId === dragId);
      if (dragIdx === -1) return prev;
      const [moved] = groups.splice(dragIdx, 1);
      const targetIdx = groups.findIndex((g) => g.nodeId === targetId);
      if (targetIdx === -1) return prev;
      groups.splice(after ? targetIdx + 1 : targetIdx, 0, moved);
      const prevGroups = groupFieldsByNode(prev);
      if (prevGroups.every((g, i) => g.nodeId === groups[i].nodeId)) return prev;
      changed = true;
      let order = 0;
      return groups.flatMap((g) => g.fields.map((f) => ({ ...f, order: order++ })));
    });
    if (changed) rebaseVerticalDrag(clientY);
  };

  const handleToggleRequired = (fieldId: string) => {
    setDraftMask((prev) => prev.map((f) => (f.id === fieldId ? { ...f, required: !f.required } : f)));
  };

  // Constrain a field to a defined list of allowed values (turns it into a dropdown),
  // or revert it back to its original free-form widget.
  const handleToggleAllowedValues = (fieldId: string) => {
    setDraftMask((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        if (f.widget === 'select' && f.baseWidget !== undefined) {
          // Revert to the original widget, dropping the constrained values.
          const reverted = { ...f, widget: f.baseWidget };
          delete reverted.baseWidget;
          delete reverted.options;
          return reverted;
        }
        // Convert to a select, seeding the list with the current default value.
        const seed = String(f.defaultValue ?? '').trim();
        return {
          ...f,
          baseWidget: f.widget,
          widget: 'select',
          options: seed ? [seed] : [],
        };
      }),
    );
  };

  const handleAddAllowedValue = (fieldId: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setDraftMask((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        const options = f.options ?? [];
        if (options.includes(trimmed)) return f;
        return { ...f, options: [...options, trimmed] };
      }),
    );
  };

  const handleRemoveAllowedValue = (fieldId: string, value: string) => {
    setDraftMask((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        const options = (f.options ?? []).filter((o) => o !== value);
        // If the removed value was the current default, fall back to the first remaining option.
        const defaultValue = String(f.defaultValue) === value ? (options[0] ?? '') : f.defaultValue;
        return { ...f, options, defaultValue };
      }),
    );
  };

  const handleSetDefaultAllowedValue = (fieldId: string, value: string) => {
    setDraftMask((prev) => prev.map((f) => (f.id === fieldId ? { ...f, defaultValue: value } : f)));
  };

  const handleToggleEnrichedOption = (fieldId: string, value: string) => {
    setDraftMask((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        const currentOptions = f.options ?? [];
        const isEnabled = currentOptions.includes(value);

        if (isEnabled) {
          // Disabling this option
          const newOptions = currentOptions.filter((o) => o !== value);
          // If this was the default, set the first remaining option as default
          const defaultValue = String(f.defaultValue) === value ? (newOptions[0] ?? '') : f.defaultValue;
          return { ...f, options: newOptions, defaultValue };
        } else {
          // Enabling this option
          const newOptions = [...currentOptions, value];
          // If there was no default or empty default, set this as default
          const defaultValue = !f.defaultValue || f.defaultValue === '' ? value : f.defaultValue;
          return { ...f, options: newOptions, defaultValue };
        }
      }),
    );
  };

  const handleUpdateNumericConstraints = (fieldId: string, updates: { min?: number; max?: number; step?: number }) => {
    setDraftMask((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        return { ...f, ...updates };
      }),
    );
  };

  const toggleCategory = (id: string) => {
    setDraftCategories((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));
  };

  const handleRemoveOutputNode = (nodeId: string) => {
    setDraftOutputNodes((prev) => prev.filter((o) => o.nodeId !== nodeId));
  };

  const handleAddOutputNode = (nodeId: string) => {
    if (!nodeId) return;
    const raw = (workflow?.rawWorkflow || {}) as Record<string, { class_type?: string }>;
    const node = raw[nodeId];
    if (!node) return;
    setDraftOutputNodes((prev) => {
      if (prev.some((o) => o.nodeId === nodeId)) return prev;
      return [...prev, { nodeId, nodeType: node.class_type || 'Unknown', outputKind: 'unknown' }];
    });
  };

  const hasChanges = workflow
    ? Object.keys(draftRawOverrides).length > 0 ||
      draftName !== workflow.name ||
      draftDesc !== ((workflow as unknown as { description?: string }).description || '') ||
      // owner is read-only, excluded from change detection
      draftStatus !== ((workflow as unknown as { status?: string }).status || 'draft') ||
      JSON.stringify(draftMask) !== JSON.stringify(workflow.inputMask) ||
      JSON.stringify(draftOutputNodes) !== JSON.stringify(workflow.analysis?.outputNodes || []) ||
      JSON.stringify(draftCategories) !== JSON.stringify(workflow.categories || []) ||
      JSON.stringify(draftAudience) !==
        JSON.stringify(
          (workflow as unknown as { audience?: WorkflowAudience }).audience || {
            type: 'specific',
            userIds: draftOwner ? [draftOwner] : [],
          },
        )
    : false;

  // A workflow with no output nodes would expose no files to users — block save
  // so a missed auto-detection can't ship a broken workflow.
  const hasNoOutputs = draftOutputNodes.length === 0;

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          {enriching && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              Enriching workflow...
            </p>
          )}
        </div>
      </div>
    );
  }

  if (error && !workflow) {
    return (
      <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8">
        <div className="max-w-2xl mx-auto">
          <Link
            to="/admin/workflows"
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to Workflows
          </Link>
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!workflow) return null;

  const isPublished = draftStatus === 'published';
  // Always edit the current state (enriched if enriched, original if not)
  const inputGroups = groupFieldsByNode(draftMask);
  const visibleCount = draftMask.filter((f) => f.visible).length;

  // Live workflow for the Test panel: the saved workflow with the current
  // in-memory edits (name, description, input mask) applied, so the preview
  // updates as fields are shown/hidden, relabeled, reordered, or constrained.
  // Use the selected test version (original or enriched)
  const testMask = workflow.enriched && testVersion === 'original' && originalMask.length > 0 ? originalMask : draftMask;
  const previewWorkflow: WorkflowDetail = {
    ...workflow,
    name: draftName,
    description: draftDesc,
    inputMask: testMask,
    categories: draftCategories,
    analysis: { ...workflow.analysis, outputNodes: draftOutputNodes },
  } as WorkflowDetail;

  return (
    <div className="h-full flex bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10">
      {/* Configuration column */}
      <div className={'flex-1 min-w-0 overflow-auto p-8 relative ' + (testing ? 'pr-3' : '')}>
        {/* Sticky Test Workflow toggle */}
        <button
          type="button"
          onClick={() => setTesting((t) => !t)}
          title={testing ? 'Close Test' : undefined}
          className={
            'sticky top-0 float-right z-30 -mt-2 mb-2 ml-4 flex items-center justify-center transition-all shadow-lg ' +
            (testing
              ? 'h-11 w-11 rounded-full bg-accent text-foreground hover:bg-accent/80'
              : 'gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow-violet-500/25 ' +
                'bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800')
          }
        >
          {testing ? (
            <X className="h-5 w-5" />
          ) : (
            <>
              <Play className="h-4 w-4" />
              Test Workflow
            </>
          )}
        </button>
        <div className="max-w-3xl mx-auto">
          <header className="mb-8">
            <Link
              to="/admin/workflows"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to Workflows
            </Link>
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Workflow Name</label>
                  <Input value={draftName} onChange={(e) => setDraftName(e.target.value)} className="text-lg font-bold h-10" />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Description</label>
                  <Input
                    value={draftDesc}
                    onChange={(e) => setDraftDesc(e.target.value)}
                    placeholder="Short description for the gallery..."
                    className="text-sm h-9"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Owner</label>
                  <Input
                    value={draftOwnerName}
                    readOnly
                    disabled
                    placeholder="Workflow owner"
                    className="text-sm h-9 bg-muted cursor-not-allowed"
                    title={draftOwner} // Show UID on hover
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Status</label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant={isPublished ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDraftStatus(isPublished ? 'draft' : 'published')}
                      className={isPublished ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      {isPublished ? <Globe className="h-4 w-4 mr-1.5" /> : <GlobeLock className="h-4 w-4 mr-1.5" />}
                      {isPublished ? 'Published — visible to users' : 'Draft — not visible to users'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {workflow.analysis.nodeCount} nodes &middot; {visibleCount} visible inputs
                  {workflow.enriched && ' · enriched'}
                  {enriching && ' · enriching...'}
                </span>

                <div className="flex gap-2">
                  {(!workflow.enriched || enrichmentFailed) && (
                    <Button variant="outline" size="sm" onClick={handleEnrich} disabled={enriching}>
                      {enriching ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                      {enrichmentFailed ? 'Retry Enrich' : 'Enrich'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !hasChanges || hasNoOutputs}
                    title={hasNoOutputs ? 'Add at least one output node before saving' : undefined}
                    className={hasChanges && !hasNoOutputs ? 'bg-violet-600 hover:bg-violet-700' : ''}
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}
                    {hasChanges ? 'Save Changes' : 'Saved'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleArchive}
                    disabled={archiving}
                    className={(workflow as WorkflowDetail & { hidden?: boolean })?.hidden ? '' : ''}
                  >
                    {archiving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {(workflow as WorkflowDetail & { hidden?: boolean })?.hidden === true ? 'Unarchiving...' : 'Archiving...'}
                      </>
                    ) : (workflow as WorkflowDetail & { hidden?: boolean })?.hidden === true ? (
                      <>
                        <ArchiveRestore className="h-4 w-4 mr-2" />
                        Unarchive
                      </>
                    ) : (
                      <>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </header>

          {(workflow as WorkflowDetail & { hidden?: boolean })?.hidden === true && (
            <div className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-300/60 dark:border-amber-500/40 rounded-md mb-6">
              <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                <Archive className="h-4 w-4" />
                This workflow is archived and hidden from the active list
              </p>
            </div>
          )}

          {hasNoOutputs && (
            <div className="p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-300/60 dark:border-amber-500/40 rounded-md mb-6">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                No output nodes selected. Auto-detection may have missed them — add at least one output node in the{' '}
                <strong>Outputs</strong> section below before saving, or users won&apos;t receive any files.
              </p>
            </div>
          )}

          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Categories */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Categories</CardTitle>
              <CardDescription>Classify this workflow so users can filter by generation type.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {WORKFLOW_CATEGORIES.map(({ id, label, icon: Icon }) => {
                  const active = draftCategories.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleCategory(id)}
                      className={
                        'flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ' +
                        (active
                          ? 'border-violet-400 bg-violet-50 text-violet-700 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-300'
                          : 'border-border/60 text-muted-foreground hover:bg-accent/50')
                      }
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                      {active && <Check className="h-3.5 w-3.5" />}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Workflow Audience */}
          <Card className="mb-6">
            <button type="button" onClick={() => setAudienceExpanded(!audienceExpanded)} className="w-full">
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <CardTitle className="text-lg flex items-center justify-between">
                  Workflow Audience
                  <ChevronDown className={'h-5 w-5 transition-transform ' + (audienceExpanded ? 'rotate-180' : '')} />
                </CardTitle>
                <CardDescription>Control who can see and run this workflow</CardDescription>
              </CardHeader>
            </button>
            {audienceExpanded && (
              <CardContent>
                <WorkflowAudienceSelector audience={draftAudience} onChange={setDraftAudience} ownerId={draftOwner} />
              </CardContent>
            )}
          </Card>

          {/* Input Form Preview with visibility toggles */}
          <Card className="mb-6">
            {/* role=button (not a real <button>) because the header nests the
                Show All / Hide All buttons — button-in-button is invalid HTML. */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setInputFormExpanded(!inputFormExpanded)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setInputFormExpanded(!inputFormExpanded);
                }
              }}
              className="w-full"
            >
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-left flex-1">
                    <CardTitle className="text-lg">Input Form Design</CardTitle>
                    <CardDescription>
                      Toggle which fields are visible to users. Hidden fields use their default values.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setDraftMask((prev) => prev.map((f) => ({ ...f, visible: true })))}
                      className={
                        'text-xs font-medium px-2.5 py-1 rounded transition-colors ' +
                        'text-green-600 bg-green-50 hover:bg-green-100 ' +
                        'dark:text-green-400 dark:bg-green-500/10'
                      }
                    >
                      <Eye className="h-3 w-3 inline mr-1" />
                      Show All
                    </button>
                    <button
                      type="button"
                      onClick={() => setDraftMask((prev) => prev.map((f) => ({ ...f, visible: false })))}
                      className="text-xs font-medium px-2.5 py-1 rounded text-muted-foreground bg-accent hover:bg-accent/80 transition-colors"
                    >
                      <EyeOff className="h-3 w-3 inline mr-1" />
                      Hide All
                    </button>
                    <ChevronDown className={'h-5 w-5 transition-transform shrink-0 ' + (inputFormExpanded ? 'rotate-180' : '')} />
                  </div>
                </div>
              </CardHeader>
            </div>
            {inputFormExpanded && (
              <CardContent>
                {inputGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground/60 text-center py-8">No configurable inputs detected</p>
                ) : (
                  <div
                    className="space-y-3"
                    // Accept the drop anywhere in the list (gaps, padding) so the
                    // browser never plays its snap-back animation.
                    onDragOver={(e) => {
                      if (dragNodeId && !dragFieldId) e.preventDefault();
                    }}
                    onDrop={(e) => e.preventDefault()}
                  >
                    {inputGroups.map((group) => {
                      const isOpen = expandedNodes.has(group.nodeId);
                      const visCount = group.fields.filter((f) => f.visible).length;
                      return (
                        <div
                          key={group.nodeId}
                          draggable={dragHandle?.type === 'node' && dragHandle.id === group.nodeId}
                          onDragStart={(e) => {
                            setDragNodeId(group.nodeId);
                            beginVerticalDrag(e.currentTarget, e.clientY);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', group.nodeId);
                            hideNativeDragImage(e);
                          }}
                          onDrag={(e) => moveVerticalDrag(e.clientY)}
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (dragNodeId && !dragFieldId && dragNodeId !== group.nodeId) {
                              e.dataTransfer.dropEffect = 'move';
                              // Insert after the target once the cursor passes its midpoint.
                              const rect = e.currentTarget.getBoundingClientRect();
                              const after = e.clientY > rect.top + rect.height / 2;
                              reorderNode(dragNodeId, group.nodeId, after, e.clientY);
                            }
                          }}
                          onDrop={(e) => {
                            // Accept the drop so the browser skips its snap-back
                            // animation — reordering already happened on dragover.
                            e.preventDefault();
                          }}
                          onDragEnd={() => {
                            endVerticalDrag();
                            setDragNodeId(null);
                            setDragHandle(null);
                          }}
                          style={dragNodeId === group.nodeId ? { position: 'relative', zIndex: 50 } : undefined}
                          className={
                            'rounded-xl border overflow-hidden ' +
                            (dragNodeId === group.nodeId
                              ? 'opacity-80 border-violet-400/60 shadow-lg'
                              : visCount === 0
                                ? 'border-dashed border-orange-400/70 bg-orange-100/70 dark:border-orange-500/50 dark:bg-orange-500/15 transition-all'
                                : 'border-solid border-border/60 opacity-100 transition-all')
                          }
                        >
                          {/* Node header */}
                          <div
                            className={'flex items-center justify-between px-4 py-3 ' + 'hover:bg-accent/30 transition-colors'}
                          >
                            <div className="flex items-center gap-2">
                              <GripVertical
                                className="h-4 w-4 text-muted-foreground/30 cursor-grab shrink-0"
                                onPointerDown={() => setDragHandle({ type: 'node', id: group.nodeId })}
                                onPointerUp={() => setDragHandle(null)}
                              />
                              <button type="button" onClick={() => toggleNode(group.nodeId)} className="flex items-center gap-2">
                                <ChevronRight
                                  className={'h-4 w-4 text-muted-foreground transition-transform ' + (isOpen ? 'rotate-90' : '')}
                                />
                                <div className="text-left">
                                  <h3 className="text-sm font-semibold text-foreground">{group.label}</h3>
                                  <p className="text-[10px] text-muted-foreground">
                                    Node {group.nodeId} &middot; {visCount}/{group.fields.length} visible
                                  </p>
                                </div>
                              </button>
                            </div>
                            <div className="flex items-center gap-1">
                              {visCount === 0 ? (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDraftMask((prev) =>
                                      prev.map((f) => (f.nodeId === group.nodeId ? { ...f, visible: true } : f)),
                                    );
                                  }}
                                  className={
                                    'text-[10px] px-1.5 py-0.5 rounded cursor-pointer transition-colors ' +
                                    'text-green-600 hover:bg-green-50 ' +
                                    'dark:text-green-400 dark:hover:bg-green-500/10'
                                  }
                                >
                                  Show
                                </span>
                              ) : (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDraftMask((prev) =>
                                      prev.map((f) => (f.nodeId === group.nodeId ? { ...f, visible: false } : f)),
                                    );
                                  }}
                                  className="text-[10px] px-1.5 py-0.5 rounded cursor-pointer text-muted-foreground hover:bg-accent transition-colors"
                                >
                                  Hide
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Collapsible fields */}
                          {isOpen && (
                            <div
                              className="px-4 pb-4 space-y-3"
                              // Accept the drop anywhere in the list (gaps, padding)
                              // so the browser never plays its snap-back animation.
                              onDragOver={(e) => {
                                if (dragFieldId) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              {group.fields
                                .sort((a, b) => a.order - b.order)
                                .map((field) => (
                                  <div
                                    key={field.id}
                                    draggable={dragHandle?.type === 'field' && dragHandle.id === field.id}
                                    onDragStart={(e) => {
                                      // Stop the node's dragstart from also firing (nested draggables).
                                      e.stopPropagation();
                                      setDragFieldId(field.id);
                                      beginVerticalDrag(e.currentTarget, e.clientY);
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', field.id);
                                      hideNativeDragImage(e);
                                    }}
                                    onDrag={(e) => {
                                      e.stopPropagation();
                                      moveVerticalDrag(e.clientY);
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (dragFieldId && dragFieldId !== field.id) {
                                        e.dataTransfer.dropEffect = 'move';
                                        // Insert after the target once the cursor passes its midpoint.
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const after = e.clientY > rect.top + rect.height / 2;
                                        reorderField(dragFieldId, field.id, after, e.clientY);
                                      }
                                    }}
                                    onDrop={(e) => {
                                      // Accept the drop so the browser skips its snap-back
                                      // animation — reordering already happened on dragover.
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onDragEnd={(e) => {
                                      e.stopPropagation();
                                      endVerticalDrag();
                                      setDragFieldId(null);
                                      setDragHandle(null);
                                    }}
                                    style={dragFieldId === field.id ? { position: 'relative', zIndex: 50 } : undefined}
                                    className={
                                      'rounded-lg border p-4 ' +
                                      (dragFieldId === field.id
                                        ? 'opacity-80 border-violet-400/60 shadow-lg '
                                        : 'transition-all ') +
                                      (field.visible
                                        ? 'border-border/60 bg-card'
                                        : 'border-dashed border-orange-400/70 bg-orange-100/70 dark:border-orange-500/50 dark:bg-orange-500/15')
                                    }
                                  >
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <GripVertical
                                          className="h-4 w-4 text-muted-foreground/30 cursor-grab"
                                          onPointerDown={() => setDragHandle({ type: 'field', id: field.id })}
                                          onPointerUp={() => setDragHandle(null)}
                                        />
                                        <span className="text-xs font-medium text-muted-foreground">{field.id}</span>
                                      </div>
                                      <div className="flex items-center gap-1.5">
                                        {field.visible && (
                                          <button
                                            type="button"
                                            onClick={() => handleToggleRequired(field.id)}
                                            className={
                                              'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ' +
                                              (field.required
                                                ? 'text-amber-600 bg-amber-50 hover:bg-amber-100 dark:text-amber-400 dark:bg-amber-500/10'
                                                : 'text-muted-foreground bg-accent hover:bg-accent/80')
                                            }
                                          >
                                            {field.required ? 'Required' : 'Optional'}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() => handleToggleVisibility(field.id)}
                                          className={
                                            'flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors ' +
                                            (field.visible
                                              ? 'text-green-600 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-500/10'
                                              : 'text-muted-foreground bg-accent hover:bg-accent/80')
                                          }
                                        >
                                          {field.visible ? (
                                            <>
                                              <Eye className="h-3 w-3" />
                                              Visible
                                            </>
                                          ) : (
                                            <>
                                              <EyeOff className="h-3 w-3" />
                                              Hidden
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                    <div className="mb-3 space-y-3">
                                      <div>
                                        <div className="flex items-center justify-between">
                                          <label className="text-[10px] text-muted-foreground">Display Label</label>
                                          <span className="text-[10px] text-muted-foreground/50 font-mono">
                                            {field.id} ({field.widget})
                                          </span>
                                        </div>
                                        <Input
                                          value={field.label}
                                          onChange={(e) =>
                                            setDraftMask((prev) =>
                                              prev.map((f) => (f.id === field.id ? { ...f, label: e.target.value } : f)),
                                            )
                                          }
                                          className="text-sm h-8 mt-0.5"
                                        />
                                      </div>
                                      <div>
                                        <label className="text-[10px] text-muted-foreground">Description (shown to users)</label>
                                        <Input
                                          value={field.description || ''}
                                          onChange={(e) =>
                                            setDraftMask((prev) =>
                                              prev.map((f) => (f.id === field.id ? { ...f, description: e.target.value } : f)),
                                            )
                                          }
                                          placeholder="Add a helpful description for this input..."
                                          className="text-sm h-8 mt-0.5"
                                        />
                                      </div>
                                    </div>
                                    {/* Edit the text value baked into the node itself.
                                        Text/textarea only — lets a manager fix a wrong
                                        prompt without re-uploading from ComfyUI. */}
                                    {(field.widget === 'text' || field.widget === 'textarea') && (
                                      <div className="mb-3">
                                        <div className="flex items-center justify-between">
                                          <label className="text-[10px] text-muted-foreground">Value in workflow</label>
                                          {field.id in draftRawOverrides && (
                                            <span className="text-[10px] text-amber-600 dark:text-amber-400">
                                              Edited — unsaved
                                            </span>
                                          )}
                                        </div>
                                        <Textarea
                                          value={draftRawOverrides[field.id] ?? bakedValue(field)}
                                          onChange={(e) => handleEditRawValue(field, e.target.value)}
                                          placeholder="Edit the value stored in this node…"
                                          className="text-sm mt-0.5 min-h-[80px] font-mono resize-y"
                                        />
                                        <p className="text-[10px] text-muted-foreground/50 mt-1">
                                          Fixes the value inside the node. Applies even when this field is hidden from users.
                                        </p>
                                      </div>
                                    )}
                                    <FieldRenderer field={field} readOnly />
                                    <NumericConstraintsEditor
                                      field={field}
                                      onUpdateConstraints={(updates) => handleUpdateNumericConstraints(field.id, updates)}
                                    />
                                    <AllowedValuesEditor
                                      field={field}
                                      onToggle={() => handleToggleAllowedValues(field.id)}
                                      onAdd={(v) => handleAddAllowedValue(field.id, v)}
                                      onRemove={(v) => handleRemoveAllowedValue(field.id, v)}
                                      onSetDefault={(v) => handleSetDefaultAllowedValue(field.id, v)}
                                      onToggleEnrichedOption={(v) => handleToggleEnrichedOption(field.id, v)}
                                    />
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Outputs — manager-curated list of nodes whose files are exposed */}
          <Card className="mb-6" ref={outputCardRef}>
            <button type="button" onClick={() => setOutputsExpanded(!outputsExpanded)} className="w-full">
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <CardTitle className="text-lg flex items-center justify-between">
                  Outputs
                  <ChevronDown className={'h-5 w-5 transition-transform ' + (outputsExpanded ? 'rotate-180' : '')} />
                </CardTitle>
                <CardDescription>
                  Nodes whose files are shown to users. Auto-detection can miss custom nodes — add or remove nodes here. Only
                  files ComfyUI saves as real outputs are ever exposed (previews and temp files are always excluded).
                </CardDescription>
              </CardHeader>
            </button>
            {outputsExpanded && (
              <CardContent className="space-y-3">
                {/* Searchable node picker — render available nodes as cards inside */}
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowOutputDropdown(!showOutputDropdown);
                      setOutputSearchQuery('');
                    }}
                    className="w-full justify-between"
                  >
                    <span className="text-xs">Add an output node…</span>
                    <ChevronDown className={'h-3 w-3 transition-transform ' + (showOutputDropdown ? 'rotate-180' : '')} />
                  </Button>
                  {showOutputDropdown && (
                    <div className="w-full mt-1 bg-background border rounded-md shadow-sm max-h-72 overflow-auto">
                      <div className="sticky top-0 bg-background p-2 border-b">
                        <Input
                          value={outputSearchQuery}
                          onChange={(e) => setOutputSearchQuery(e.target.value)}
                          placeholder="Search by node type, title or ID..."
                          className="text-sm h-8"
                          autoFocus
                        />
                      </div>
                      {(() => {
                        const query = outputSearchQuery.trim().toLowerCase();
                        const available = Object.entries(
                          (workflow.rawWorkflow || {}) as Record<string, { class_type?: string; _meta?: { title?: string } }>,
                        )
                          .filter(([nodeId]) => !draftOutputNodes.some((o) => o.nodeId === nodeId))
                          .filter(([nodeId, node]) => {
                            if (!query) return true;
                            return (
                              nodeId.toLowerCase().includes(query) ||
                              (node.class_type || '').toLowerCase().includes(query) ||
                              (node._meta?.title || '').toLowerCase().includes(query)
                            );
                          });

                        if (available.length === 0) {
                          return (
                            <div className="px-3 py-2 text-xs text-muted-foreground">
                              {query ? 'No nodes match your search' : 'All nodes already added'}
                            </div>
                          );
                        }

                        return available.map(([nodeId, node]) => (
                          <button
                            key={nodeId}
                            type="button"
                            onClick={() => {
                              handleAddOutputNode(nodeId);
                              setShowOutputDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2.5"
                          >
                            <div className="w-8 h-8 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                              <Box className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">
                                {node._meta?.title ? `${node._meta.title} — ` : ''}
                                {node.class_type || 'Unknown'}
                              </div>
                              <div className="text-[11px] text-muted-foreground">Node {nodeId}</div>
                            </div>
                            <Plus className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </div>

                {/* Selected output nodes */}
                {draftOutputNodes.length > 0 ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {draftOutputNodes.map((output) => {
                      const Icon = outputIcons[output.outputKind] || Box;
                      return (
                        <div
                          key={output.nodeId}
                          className="flex items-center gap-3 p-3 rounded-lg bg-accent/50 border border-border/40"
                        >
                          <div
                            className={
                              'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ' +
                              (output.outputKind === 'image'
                                ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                                : output.outputKind === 'video'
                                  ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400'
                                  : 'bg-muted text-muted-foreground')
                            }
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{output.nodeType}</p>
                            <span className="text-[11px] text-muted-foreground">Node {output.nodeId}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveOutputNode(output.nodeId)}
                            className="text-muted-foreground/50 hover:text-destructive transition-colors shrink-0"
                            title="Remove from outputs"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground/60 text-center py-4">
                    No output nodes selected — this workflow won&apos;t expose any files to users.
                  </p>
                )}
              </CardContent>
            )}
          </Card>

          {/* Raw JSON */}
          <Card>
            <button type="button" onClick={() => setRawExpanded(!rawExpanded)} className="w-full">
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                  Raw Workflow JSON
                  <ChevronDown className={'h-4 w-4 transition-transform ' + (rawExpanded ? 'rotate-180' : '')} />
                </CardTitle>
              </CardHeader>
            </button>
            {rawExpanded && (
              <CardContent className="pt-0">
                <pre className="text-xs text-foreground bg-accent/50 p-4 rounded-lg overflow-x-auto max-h-96 overflow-y-auto">
                  {JSON.stringify(workflow.rawWorkflow, null, 2)}
                </pre>
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Live end-user preview / test panel */}
      {testing && (
        <div className="w-[440px] shrink-0 border-l border-border/60 bg-background/50 overflow-auto relative">
          <div className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur">
            <div className="flex items-center gap-2 px-4 py-2.5">
              <Play className="h-3.5 w-3.5 text-violet-500" />
              <span className="text-xs font-semibold text-foreground">Test — what users see</span>
              <span className="text-[10px] text-muted-foreground">Live preview of unsaved edits</span>
            </div>
            {/* Version selector for enriched workflows */}
            {workflow.enriched && originalMask.length > 0 && (
              <div className="flex gap-1 px-4 pb-2">
                <button
                  type="button"
                  onClick={() => setTestVersion('original')}
                  className={
                    'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ' +
                    (testVersion === 'original'
                      ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400'
                      : 'bg-accent/50 text-muted-foreground hover:bg-accent hover:text-foreground')
                  }
                >
                  Original
                </button>
                <button
                  type="button"
                  onClick={() => setTestVersion('enriched')}
                  className={
                    'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1 ' +
                    (testVersion === 'enriched'
                      ? 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400'
                      : 'bg-accent/50 text-muted-foreground hover:bg-accent hover:text-foreground')
                  }
                >
                  <Sparkles className="h-3 w-3" />
                  Enriched
                </button>
              </div>
            )}
          </div>
          {/* Live preview driven by the in-memory draft, not a refetch */}
          <RunPage workflowOverride={previewWorkflow} />
        </div>
      )}
    </div>
  );
}
